/**
 * useSpeechToText — Stage 10 Voice Input.
 *
 * Browser-native speech recognition using Web Speech API. Works on Chrome
 * (desktop + Android), Edge, and Safari (iOS 14.5+ and macOS).
 * Falls back gracefully — `supported` flag tells callers when to hide the mic.
 *
 * Lifecycle:
 *   const { supported, listening, transcript, interim, start, stop, reset, error } = useSpeechToText();
 *   start()  → triggers the browser permission prompt the first time.
 *   stop()   → ends recognition and finalises the transcript.
 *   reset()  → clears transcript + interim for a fresh capture.
 *
 * Notes:
 *   • `interim` is the live, low-confidence string while the user is speaking.
 *   • `transcript` is the high-confidence finalised result after stop.
 *   • iOS Safari requires recognition to be triggered by a user gesture
 *     (tap), which our callers honour.
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const getRecognitionCtor = () => {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

export const useSpeechToText = ({ language = 'en-US', interimResults = true } = {}) => {
  const [supported] = useState(() => !!getRecognitionCtor());
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim] = useState('');
  const [error, setError] = useState(null);

  // Track the recognition instance + the last-final transcript so we can
  // accumulate across multiple `result` events without losing previous chunks.
  const recRef = useRef(null);
  const finalRef = useRef('');

  // Cleanup helper — exposed for unmount + manual reset.
  const teardown = useCallback(() => {
    const rec = recRef.current;
    if (!rec) return;
    try { rec.onresult = null; rec.onend = null; rec.onerror = null; rec.stop(); }
    catch { /* already stopped */ }
    recRef.current = null;
  }, []);

  const reset = useCallback(() => {
    finalRef.current = '';
    setTranscript('');
    setInterim('');
    setError(null);
  }, []);

  const start = useCallback(() => {
    if (!supported) { setError('Voice input is not supported on this browser.'); return; }
    if (recRef.current) return; // already running
    setError(null);

    const Ctor = getRecognitionCtor();
    const rec = new Ctor();
    rec.lang = language;
    rec.continuous = true;
    rec.interimResults = interimResults;

    rec.onresult = (event) => {
      let interimChunk = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) finalRef.current += r[0].transcript;
        else interimChunk += r[0].transcript;
      }
      setInterim(interimChunk);
      setTranscript(finalRef.current.trim());
    };

    rec.onerror = (e) => {
      // 'no-speech' fires after silence — treat as a soft stop, not an error.
      if (e?.error === 'no-speech' || e?.error === 'aborted') return;
      setError(e?.error || 'speech_error');
      setListening(false);
    };

    rec.onend = () => {
      setListening(false);
      setInterim('');
      // Detach so a fresh start() builds a new instance.
      recRef.current = null;
    };

    try {
      rec.start();
      recRef.current = rec;
      setListening(true);
    } catch (err) {
      setError(err?.message || 'speech_start_failed');
      recRef.current = null;
      setListening(false);
    }
  }, [supported, language, interimResults]);

  const stop = useCallback(() => {
    const rec = recRef.current;
    if (!rec) return;
    try { rec.stop(); } catch { /* noop */ }
  }, []);

  // Hard cleanup on unmount.
  useEffect(() => () => teardown(), [teardown]);

  return { supported, listening, transcript, interim, error, start, stop, reset };
};

export default useSpeechToText;
